/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Fragment, ReactElement, useCallback, useState } from 'react';
import { SkeletonBlock, SkeletonLoader } from '@zapier/design-system';
import { Builder } from '@builder.io/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { useSplitTestContext } from '../SplitTest/SplitTest';

type Props = {
  children: ReactElement;
  isControl: boolean;
  preview: boolean;
  treatmentName: string;
};

const Styles = labelStyles('SplitTreatment', {
  invisibleChildren: css({
    opacity: '0',
    position: 'absolute',
  }),
});

export function SplitTreatment({
  children,
  isControl,
  preview,
  treatmentName,
}: Props) {
  const [height, setHeight] = useState(0);
  const { isReady, treatment } = useSplitTestContext();

  /**
   * In order to be able to display a loader while split is loading,
   * we need to set the height of the loader to the height that the children will occupy.
   * We do this by rendering the first child in a div with opacity 0 in order to get its height and then pass that to the loader to use while loading.
   */
  const handleChildRef = useCallback((node) => {
    setHeight((prevState) => {
      const nodeHeight = node?.getBoundingClientRect()?.height ?? 0;

      if (prevState !== nodeHeight) return nodeHeight;

      return prevState;
    });
  }, []);

  const isPreviewOrEditing = Builder.isEditing || Builder.isPreviewing;

  const shouldShowTreatment =
    treatment === treatmentName ||
    (isControl && treatment === 'control') ||
    (isPreviewOrEditing && preview);

  if (!shouldShowTreatment) return <Fragment />;

  return !isReady ? (
    <Fragment>
      <SkeletonLoader>
        <SkeletonBlock height={height} width="100%"></SkeletonBlock>
      </SkeletonLoader>
      <div css={Styles.invisibleChildren} ref={handleChildRef}>
        {children}
      </div>
    </Fragment>
  ) : (
    <Fragment>{children}</Fragment>
  );
}
